import { GatewayContact } from "@conformite/gateway";
import {
  ColumnDefinition,
  FilterSearch,
  IconStatus,
  InputSearch,
  ListTemplate,
  Pagination,
  SidePane,
  Table,
  useDialog,
  Button,
  displayErrorToast,
} from "@endya-conformite/ui-kit";
import TrashIcon from "@endya-conformite/ui-kit/src/assets/icons/trash.svg?react";
import { recordToSelectOptions } from "@src/adhesion/AdhesionData/adhesionData";
import { GatewayAssujettiApi } from "@src/api/assujetti.api";
import { RoleAssujettiLabels } from "@src/assujettis/AssujettiConsts";
import { AssujettiFilterHookReturn } from "@src/assujettis/AssujettiMainList/useAssujettiFilter";
import { AssujettiPaneContent } from "@src/assujettis/AssujettiPaneContent/AssujettiPaneContent";
import { useAssujettiPane } from "@src/assujettis/AssujettiPaneContent/useAssujettiPane";
import { useAssujetisAvancement } from "@src/assujettis/useAssujettiAvancement";
import { useIsAllowed } from "@src/session/useIsAllowed";
import { StoreEntryFetchStatus } from "@src/store/store";
import { Assujetti } from "@src/store/store.definition";
import { useEntreprises } from "@src/store/store.entreprise";
import { useMe } from "@src/store/store.me";
import { FilterDropdown } from "../Filter/FilterDropdown";
import { FilterMultiSelect } from "../Filter/FilterMultiSelect";
import { SortDropdown } from "../Filter/SortDropdown";
import { SortSelect } from "../Filter/SortSelect";
import styles from "./AssujettiList.module.scss";

const insertIf = (condition: boolean, value: ColumnDefinition<Assujetti>) =>
  condition ? [value] : [];

export type AssujettiComponent = (props: {
  assujetti: Assujetti;
}) => JSX.Element;

export type AssujettiListProps = {
  onRowClick?: (row: Assujetti, index: number) => void;
  HeaderActions?: React.ReactNode;
  removeEntries: (ids: string[], millesime: number) => void;
  AssujetiConformite: AssujettiComponent;
  setEntry: (entry: Assujetti) => void;
  millesime: number;
} & AssujettiFilterHookReturn;

export function AssujettiList({
  onRowClick,
  content: assujettis,
  numberOfContentUnpaginated,
  content,
  page,
  setPage,
  pageSize,
  filtersSelected,
  setFilterSelected,
  sortSelected,
  setSortSelected,
  HeaderActions,
  removeEntries,
  AssujetiConformite,
  setEntry,
  millesime,
}: AssujettiListProps) {
  const { hasAllowedRole } = useIsAllowed();
  const { entreprisesBySiren, status } = useEntreprises();
  const { openConfirmDialog } = useDialog();
  const { getAssujetisAvancement } = useAssujetisAvancement();
  const { me } = useMe();
  const [assujettiPaneState, setAssujettiPaneState] = useAssujettiPane();

  function isConform(assujetti: Assujetti) {
    return getAssujetisAvancement(assujetti) === 100;
  }
  async function deleteMultipleAssujetis(selectedRows: Assujetti[]) {
    if (!millesime) return;
    const shouldContinue = await openConfirmDialog({
      title:
        "Êtes-vous sur(e) de vouloir supprimer ces personnes assujetties ?",
      message: "Cette action est irréversible.",
      confirmText: "Confirmer",
      cancelText: "Annuler",
    });

    if (!shouldContinue) return;

    await GatewayAssujettiApi.deleteMultiple(
      selectedRows.map((r) => r.id),
      millesime
    );
    removeEntries(
      selectedRows.map((r) => r.id),
      millesime
    );
  }
  async function deleteOneAssujeti(assujetti: Assujetti) {
    if (!millesime) return;
    const shouldContinue = await openConfirmDialog({
      title:
        "Êtes-vous sur(e) de vouloir supprimer cette personne assujettie ?",
      message: "Cette action est irréversible.",
      confirmText: "Confirmer",
      cancelText: "Annuler",
    });

    if (!shouldContinue) return;

    await GatewayAssujettiApi.deleteMultiple([assujetti.id], millesime);
    removeEntries([assujetti.id], millesime);
  }

  if (!me) return <div />;

  const ctaNoData =
    !content.length &&
    millesime === me?.entreprisePrincipale.millesimeActuel ? (
      <Button
        variant="outline"
        onClick={() =>
          setAssujettiPaneState((prevState) => ({
            assujetti: null,
            initialPage: "information-personne",
            isOpen: true,
            paneSession:
              prevState.assujetti === null &&
              prevState.initialPage === "information-personne"
                ? prevState.paneSession
                : assujettiPaneState.paneSession + 1,
          }))
        }
      >
        Ajouter vos personnes assujetties
      </Button>
    ) : null;

  return (
    <>
      <ListTemplate
        isLoading={status === StoreEntryFetchStatus.Loading}
        header={
          <FilterSearch
            search={
              <InputSearch
                placeholder="Prénom, Nom, Raison sociale"
                name="search"
                value={filtersSelected?.search[0]}
                onChange={(e) => {
                  setFilterSelected("search", [e.target.value]);
                }}
              />
            }
            filter={
              <>
                <FilterDropdown
                  filtersSelected={filtersSelected}
                  blacklist={["search"]}
                >
                  <FilterMultiSelect
                    name="filterConformite"
                    label="Conformité"
                    filtersSelected={filtersSelected}
                    setFilterSelected={setFilterSelected}
                    keyFitler="conformite"
                    options={[
                      {
                        value: "true",
                        label: "Conforme",
                      },
                      {
                        value: "false",
                        label: "Non conforme",
                      },
                    ]}
                  />
                  <FilterMultiSelect
                    name="filterFonction"
                    label="Fonction"
                    filtersSelected={filtersSelected}
                    setFilterSelected={setFilterSelected}
                    keyFitler="fonction"
                    options={recordToSelectOptions(RoleAssujettiLabels)}
                  />
                </FilterDropdown>
                <SortDropdown>
                  <SortSelect
                    name="sort"
                    sortSelected={sortSelected}
                    setSortSelected={setSortSelected}
                    options={[
                      {
                        label: "Prénom Nom",
                        value: "name",
                      },
                      {
                        label: "Conformite",
                        value: "conformite",
                      },
                    ]}
                  />
                </SortDropdown>
              </>
            }
            action={HeaderActions}
          />
        }
        table={
          !!assujettis &&
          !!entreprisesBySiren && (
            <Table<Assujetti>
              selectionOptions={
                hasAllowedRole(
                  GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR,
                  GatewayContact.ContactRoleUtilisateur.RESPONSABLE_CONFORMITE
                )
                  ? {
                      renderActions: (selectedRows) => (
                        <Button
                          iconVariant="left"
                          className={styles.deleteMultipleButton}
                          icon={<TrashIcon />}
                          onClick={() => {
                            deleteMultipleAssujetis(selectedRows).catch(() => {
                              displayErrorToast(
                                "une erreur est survenue lors de la suppression"
                              );
                            });
                          }}
                          variant="text"
                        >
                          Supprimer les personnes
                        </Button>
                      ),
                    }
                  : undefined
              }
              data={content}
              onRowClick={onRowClick}
              columns={[
                {
                  key: "validSociete",
                  size: "auto",
                  render: (item) => <IconStatus isValid={isConform(item)} />,
                },
                {
                  key: "name",
                  title: "Prénom Nom",
                  render: (assujetti) => (
                    <p>{`${assujetti.surname} ${assujetti.name}`}</p>
                  ),
                  size: 1.5,
                },
                {
                  key: "company",
                  title: "Société",
                  size: 1,
                  render: (assujetti) => (
                    <p>
                      {
                        entreprisesBySiren[assujetti.sirenCompany]
                          ?.raisonSociale
                      }
                    </p>
                  ),
                },
                {
                  key: "role",
                  title: "Fonction",
                  size: 1,
                  render: (assujetti) => (
                    <p>{RoleAssujettiLabels[assujetti.role]}</p>
                  ),
                },
                {
                  key: "compliance",
                  title: "Conformité",
                  size: "auto",
                  render: (item) => (
                    <AssujetiConformite assujetti={item} />
                    // <ComplianceProgressBar
                    //   progress={getAssujetisAvancement(item)}
                    // />
                  ),
                },
                ...insertIf(
                  hasAllowedRole(
                    GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR,
                    GatewayContact.ContactRoleUtilisateur.RESPONSABLE_CONFORMITE
                  ),
                  {
                    key: "actions",
                    size: "auto",
                    render: (assujetti) => (
                      <div className={styles.actions}>
                        <Button
                          type="button"
                          ariaLabel={`Supprimer ${assujetti.surname} ${assujetti.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteOneAssujeti(assujetti).catch(() => {
                              displayErrorToast(
                                "une erreur est survenue lors de la suppression"
                              );
                            });
                          }}
                          className={styles.trashButton}
                          variant="text"
                          iconVariant="left"
                          icon={<TrashIcon />}
                        />
                      </div>
                    ),
                  }
                ),
              ]}
              textNoData="Aucune personne assujettie renseignée"
              ctaNoData={ctaNoData}
            />
          )
        }
        footer={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={numberOfContentUnpaginated}
            onChange={setPage}
          />
        }
      />
      <SidePane
        isOpen={assujettiPaneState.isOpen}
        setIsOpen={(nextIsOpen) =>
          setAssujettiPaneState((prevSate) => ({
            ...prevSate,
            isOpen: nextIsOpen,
          }))
        }
      >
        {(closePane) => (
          <AssujettiPaneContent
            readonly={me.entreprisePrincipale.millesimeActuel !== millesime}
            key={assujettiPaneState.paneSession}
            millesime={millesime}
            setEntry={(assujetti) => {
              setEntry(assujetti);
              setAssujettiPaneState((prevState) => ({
                ...prevState,
                assujetti,
              }));
            }}
            closePane={closePane}
            assujetti={assujettiPaneState.assujetti}
            initialPage={assujettiPaneState.initialPage}
          />
        )}
      </SidePane>
    </>
  );
}
